import { pickLocalized } from "./localization";
// A class has ~20 s per question. Five long options are too many to read
// in that time (session UWR9ZP, 2026-10-06), so a question never shows
// more than four.
export const MAX_OPTIONS = 4;
// How many questions the teacher's review screen preselects when a
// lesson is loaded by its code. The teacher can add the rest by hand.
export const INITIAL_QUESTION_COUNT = 5;
function textLength(t) {
    return pickLocalized(t, "en").length;
}
// Keep every correct option plus wrong ones picked at random, up to
// `max`. Kept options stay in their original relative order. The drop is
// random, not by length: keeping the shortest wrong options made the
// correct answer relatively the longest, a "pick the long one" cue.
// Correct options are never dropped, so a multiChoice question with more
// than `max` correct options comes back with more than `max` options.
// Call it once per question and reuse the result — every call can keep
// a different set.
export function limitOptions(q, max = MAX_OPTIONS, rng = Math.random) {
    if (q.options.length <= max)
        return q;
    const correctCount = q.options.filter((o) => o.isCorrect).length;
    const wrong = shuffle(q.options.filter((o) => !o.isCorrect), rng);
    const keep = new Set(wrong.slice(0, Math.max(0, max - correctCount)));
    return {
        ...q,
        options: q.options.filter((o) => o.isCorrect || keep.has(o)),
    };
}
// Fisher-Yates over a copy; the input is not mutated.
function shuffle(items, rng) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
// Returns a new question; the input is not mutated.
// The lesson API lists the correct option first, so without this the
// answer is always the first tile. Options that refer to each other by
// position ("all of the above") are not handled; none were found in the
// lessons sampled at review (BIH300/700/750).
export function shuffleOptions(q, rng = Math.random) {
    return { ...q, options: shuffle(q.options, rng) };
}
// "True" and "False" as the lesson API spells them across its languages,
// lowercased with trailing punctuation removed (see normalizeWord). The
// API has no true/false marker — a T/F question is a singleChoice
// question with two options — so the option text is the only signal.
// English alone is not enough: BIH300 has "Flase" in English while every
// other language is spelled right, so an option counts as True or False
// by its languages' majority.
const TRUE_WORDS = new Set([
    "true", "sant", "wahr", "vrai", "verdadero", "verdadeiro", "vero",
    "waar", "prawda", "totta", "oikein", "adevărat", "вярно", "истинный",
    "истина", "верно", "правда", "sax", "doğru", "صحيح", "صحیح", "सही",
    "சரி", "จริง", "ሓቂ",
]);
const FALSE_WORDS = new Set([
    "false", "falskt", "usant", "falsch", "faux", "falso", "fals",
    "onwaar", "fałsz", "tarua", "väärin", "невярно", "грешно", "ложь",
    "ложный", "неверно", "неправда", "помилковий", "хибно", "khalad",
    "yanliş", "خطأ", "غلط", "गलत", "தவறு", "เท็จ", "ሓሶት",
]);
function normalizeWord(s) {
    return s
        .normalize("NFC")
        .toLowerCase()
        // Turkish dotless ı: "YANLIŞ".toLowerCase() is "yanliş" but the
        // lowercase source spelling is "yanlış". Fold both to one form.
        .replace(/ı/g, "i")
        .replace(/[\s.!。።]+$/u, "")
        .trim();
}
function trueFalseValue(t) {
    const texts = typeof t === "string" ? [t] : Object.values(t);
    let votes = 0;
    for (const text of texts) {
        const w = normalizeWord(text);
        if (TRUE_WORDS.has(w))
            votes++;
        else if (FALSE_WORDS.has(w))
            votes--;
    }
    return votes > 0 ? true : votes < 0 ? false : null;
}
// True when the question is a two-option True/False question.
export function isTrueFalse(q) {
    if (q.options.length !== 2)
        return false;
    const [a, b] = q.options.map((o) => trueFalseValue(o.text));
    return a !== null && b !== null && a !== b;
}
// The order every student and the host see.
// - True/False: always True first. The lessons mix the order (BIH750 has
//   False first in one question and True first in the next); flipping it
//   from question to question adds load and frustration for nothing.
// - Everything else is shuffled, two-option questions included: the
//   lesson API lists the correct option first, so lesson order would put
//   the answer on A every time.
export function orderOptions(q, rng = Math.random) {
    if (isTrueFalse(q)) {
        const options = [...q.options].sort((a, b) => Number(trueFalseValue(b.text)) - Number(trueFalseValue(a.text)));
        return { ...q, options };
    }
    return shuffleOptions(q, rng);
}
// Everything a question goes through once, at session create: trim to
// `MAX_OPTIONS`, then put the options in their final order.
export function prepareQuestion(q, rng = Math.random) {
    return orderOptions(limitOptions(q, MAX_OPTIONS, rng), rng);
}
// Characters a student has to read: question plus every option.
export function questionLength(q) {
    return (textLength(q.text) +
        q.options.reduce((sum, o) => sum + textLength(o.text), 0));
}
// The `count` shortest questions, returned in their original order.
// Ties go to the question that comes first in the lesson.
export function pickInitialQuestions(questions, count = INITIAL_QUESTION_COUNT) {
    if (questions.length <= count)
        return questions;
    const chosen = new Set(questions
        .map((q, i) => ({ q, i, len: questionLength(q) }))
        .sort((a, b) => a.len - b.len || a.i - b.i)
        .slice(0, count)
        .map(({ q }) => q));
    return questions.filter((q) => chosen.has(q));
}
// The questions the review screen starts with selected. A whole lesson
// is too long for one class, so it starts with the shortest few. A staged
// or handed-off set was already chosen by whoever sent it, so all of it
// starts selected.
export function initialSelection(questions, source) {
    return source === "lesson" ? pickInitialQuestions(questions) : questions;
}
//# sourceMappingURL=options.js.map